<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div class="min-w-0 flex-1">
                    <h1 class="stage-page-title">{{ $t('Orphaned files analysis') }}</h1>
                </div>
                <div class="flex items-center">
                    <router-link
                        class="btn-stage-secondary mr-3"
                        to="/workspace/admin/settings"
                    >
                        {{ $t('Back') }}
                    </router-link>
                    <button
                        id="orphaned-files-filter-button"
                        :aria-label="$t('Filters')"
                        class="btn-stage-icon mr-3 stage-filter-icon-button"
                        type="button"
                        @click="openFiltersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.filter-regular"></svg-vue>
                    </button>
                    <button
                        type="button"
                        class="btn-stage-icon"
                        :disabled="analyzing || deleting"
                        @click="runAnalysis"
                    >
                        <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading || analyzing || deleting"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div class="border-b border-bordergray-100 px-4 py-3 sm:px-6 flex flex-wrap items-center justify-between gap-3">
                            <label class="inline-flex items-center text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    class="form-checkbox mr-2"
                                    :checked="allSelected"
                                    :disabled="displayedItems.length < 1 || deleting"
                                    @change="toggleSelectAll($event.target.checked)"
                                >
                                    {{ $t('Select all') }}
                                </label>
                            <div class="inline-flex items-center gap-2">
                                <button
                                    type="button"
                                    class="btn btn-red rounded shadow-sm"
                                    :disabled="selectedIds.length < 1 || deleting"
                                    @click="deleteSelected"
                                >
                                    {{ $t('Delete selected') }} ({{ selectedIds.length }})
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-red rounded shadow-sm"
                                    :disabled="summary.total_count < 1 || deleting"
                                    @click="deleteAllInScope"
                                >
                                    {{ $t('Delete all found') }}
                                </button>
                            </div>
                        </div>

                        <template v-if="displayedItems.length > 0">
                            <div class="hidden sm:block">
                                <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                    <table class="table-listview table-listview--zebra">
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th table-listview-l-th-sticky w-12">
                                                <span class="sr-only">{{ $t('Select') }}</span>
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky">
                                                {{ $t('File') }}
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky">
                                                {{ $t('Storage path') }}
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky w-32 whitespace-nowrap">
                                                {{ $t('Size') }}
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky w-44 whitespace-nowrap">
                                                {{ $t('Created at') }}
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky table-listview-l-th-right w-28 whitespace-nowrap">
                                                {{ $t('Actions') }}
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white">
                                        <tr v-for="item in displayedItems" :key="'orphaned-file-row-' + item.id">
                                            <td class="table-listview-l-td">
                                                <input
                                                    v-model="selectedIds"
                                                    :value="Number(item.id)"
                                                    type="checkbox"
                                                    class="form-checkbox"
                                                    :disabled="deleting"
                                                >
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap">
                                                <div class="text-sm font-medium text-gray-900 truncate">{{ item.name }}</div>
                                                <div class="text-sm text-gray-500 truncate">{{ item.mime }}</div>
                                            </td>
                                            <td class="table-listview-l-td">
                                                <div class="text-sm text-gray-700 break-all">{{ item.disk }}/{{ item.path }}</div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-nowrap">
                                                <div class="text-sm text-gray-700">{{ item.size_readable }}</div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-nowrap">
                                                <div class="text-sm text-gray-500">{{ formatDate(item.created_at) }}</div>
                                            </td>
                                            <td class="table-listview-l-td text-right" @click.stop>
                                                <div class="inline-flex items-center justify-end w-full">
                                                    <button
                                                        v-if="canPreview(item) && hasPreviewSource(item)"
                                                        type="button"
                                                        class="inline-flex"
                                                        :disabled="deleting"
                                                        :title="$t('Preview')"
                                                        :aria-label="$t('Preview')"
                                                        @click="openPreview(item)"
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4 text-gray-700">
                                                            <path d="M1.5 12C3.9 7.8 7.6 5.5 12 5.5c4.4 0 8.1 2.3 10.5 6.5-2.4 4.2-6.1 6.5-10.5 6.5-4.4 0-8.1-2.3-10.5-6.5z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                                                            <circle cx="12" cy="12" r="3.2" stroke="currentColor" stroke-width="1.7"/>
                                                        </svg>
                                                    </button>
                                                    <a
                                                        v-if="hasDownload(item)"
                                                        :href="withToken(item.download_url)"
                                                        class="inline-flex ml-3"
                                                        :title="$t('Download')"
                                                        :aria-label="$t('Download')"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                    >
                                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.arrow-to-bottom-light"></svg-vue>
                                                    </a>
                                                    <button
                                                        type="button"
                                                        class="inline-flex ml-3"
                                                        :disabled="deleting"
                                                        :title="$t('Delete')"
                                                        :aria-label="$t('Delete')"
                                                        @click="deleteSingle(item)"
                                                    >
                                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class="sm:hidden p-4 space-y-3">
                                <div
                                    v-for="item in displayedItems"
                                    :key="'orphaned-file-mobile-' + item.id"
                                    class="border border-bordergray-100 rounded p-3 bg-white"
                                >
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <div class="text-sm font-medium text-gray-900 truncate">{{ item.name }}</div>
                                            <div class="text-xs text-gray-500 truncate">{{ item.mime }}</div>
                                        </div>
                                        <input
                                            v-model="selectedIds"
                                            :value="Number(item.id)"
                                            type="checkbox"
                                            class="form-checkbox mt-1"
                                            :disabled="deleting"
                                        >
                                    </div>
                                    <div class="mt-2 text-xs text-gray-600 break-all">{{ item.disk }}/{{ item.path }}</div>
                                    <div class="mt-1 text-xs text-gray-500">{{ item.size_readable }} • {{ formatDate(item.created_at) }}</div>
                                    <div class="mt-3 inline-flex items-center">
                                        <button
                                            v-if="canPreview(item) && hasPreviewSource(item)"
                                            type="button"
                                            class="inline-flex"
                                            :disabled="deleting"
                                            :title="$t('Preview')"
                                            :aria-label="$t('Preview')"
                                            @click="openPreview(item)"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4 text-gray-700">
                                                <path d="M1.5 12C3.9 7.8 7.6 5.5 12 5.5c4.4 0 8.1 2.3 10.5 6.5-2.4 4.2-6.1 6.5-10.5 6.5-4.4 0-8.1-2.3-10.5-6.5z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                                                <circle cx="12" cy="12" r="3.2" stroke="currentColor" stroke-width="1.7"/>
                                            </svg>
                                        </button>
                                        <a
                                            v-if="hasDownload(item)"
                                            :href="withToken(item.download_url)"
                                            class="inline-flex ml-3"
                                            :title="$t('Download')"
                                            :aria-label="$t('Download')"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.arrow-to-bottom-light"></svg-vue>
                                        </a>
                                        <button
                                            type="button"
                                            class="inline-flex ml-3"
                                            :disabled="deleting"
                                            :title="$t('Delete')"
                                            :aria-label="$t('Delete')"
                                            @click="deleteSingle(item)"
                                        >
                                            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.trash-alt-regular"></svg-vue>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="!loading && !analyzing">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filtersSidebar"
                        class="stage-filter-sidebar stage-filter-panel tickets-filter-sidebar w-80 overflow-auto z-20"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="grid grid-cols-3 py-1">
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Search') }}</label>
                                    <div class="relative flex-grow focus-within:z-10">
                                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                        </div>
                                        <input
                                            v-model="filters.search"
                                            :placeholder="$t('Search')"
                                            :aria-label="$t('Search')"
                                            class="stage-filter-input form-input block pl-10 text-sm transition ease-in-out duration-150"
                                            @change="syncOrphanedImagesRouteState"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Area') }}</label>
                                    <div class="stage-filter-sort-select-wrap">
                                        <select
                                            v-model="filters.area"
                                            class="stage-filter-sort-select block form-select text-sm leading-5 font-medium transition ease-in-out duration-150"
                                            @change="syncOrphanedImagesRouteState"
                                        >
                                            <option
                                                v-for="option in areaOptions"
                                                :key="'area-option-' + option.value"
                                                :value="option.value"
                                            >
                                                {{ option.label }}
                                            </option>
                                        </select>
                                        <span class="stage-filter-select-icon">
                                            <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                        </span>
                                    </div>
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Only older than (hours)') }}</label>
                                    <input
                                        v-model.number="filters.olderThanHours"
                                        min="0"
                                        max="720"
                                        type="number"
                                        class="stage-filter-input form-input block text-sm transition ease-in-out duration-150"
                                        @change="syncOrphanedImagesRouteState"
                                    >
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Max result count') }}</label>
                                    <input
                                        v-model.number="filters.limit"
                                        min="1"
                                        max="1000"
                                        type="number"
                                        class="stage-filter-input form-input block text-sm transition ease-in-out duration-150"
                                        @change="syncOrphanedImagesRouteState"
                                    >
                                </div>
                                <div class="col-span-3 mb-3">
                                    <button
                                        type="button"
                                        class="btn-stage-primary w-full"
                                        :disabled="analyzing || deleting"
                                        @click="runAnalysis"
                                    >
                                        {{ $t('Run analysis') }}
                                    </button>
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Overview') }}</label>
                                    <div class="text-sm text-gray-600">
                                        {{ $t('Found') }}: {{ summary.total_count }}
                                    </div>
                                    <div class="text-sm text-gray-600">
                                        {{ $t('Total size') }}: {{ summary.total_size_readable }}
                                    </div>
                                    <div class="text-sm text-gray-600">
                                        {{ $t('Loaded list') }}: {{ displayedItems.length }}<span v-if="summary.has_more">+</span>
                                    </div>
                                </div>
                                <div v-if="hasActiveFilters" class="col-span-3">
                                    <button
                                        id="orphaned-files-filter-reset-btn"
                                        type="button"
                                        class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                        @click="resetFilters"
                                    >
                                        {{ $t('Reset selection') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>

                <nav class="bg-white w-full px-4 py-3 flex items-center justify-between border-t border-ultralight sm:px-6">
                    <div class="hidden sm:block">
                        <p class="text-sm leading-5 text-gray-700">
                            {{ $t('Showing') }}
                            <span class="font-medium">{{ displayStart }}</span>
                            {{ $t('to') }}
                            <span class="font-medium">{{ displayEnd }}</span>
                            {{ $t('of') }}
                            <span class="font-medium">{{ summary.total_count }}</span>
                            {{ $t('results') }}
                        </p>
                    </div>
                    <div class="flex-1 flex justify-between sm:justify-end">
                        <button
                            type="button"
                            class="pagination-link"
                            :disabled="analyzing || deleting"
                            @click="runAnalysis"
                        >
                            {{ $t('Refresh') }}
                        </button>
                    </div>
                </nav>
            </div>
        </div>

        <div v-if="preview.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" @click.self="closePreview">
            <div class="bg-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div class="text-sm font-semibold text-gray-900 truncate">
                        {{ preview.item ? preview.item.name : '' }}
                    </div>
                    <button type="button" class="btn btn-gray rounded shadow-sm" @click="closePreview">
                        {{ $t('Close') }}
                    </button>
                </div>
                <div class="p-4 overflow-auto flex-1">
                    <div v-if="preview.loading" class="text-sm text-gray-500">{{ $t('Loading preview...') }}</div>
                    <img v-else-if="preview.url" :src="preview.url" alt="" class="max-w-full h-auto mx-auto rounded border border-gray-200">
                    <div v-else class="text-sm text-gray-500">{{ $t('Preview not available') }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {getAuthToken} from '@/utils/auth-token';
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: 'settings-orphaned-images',
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const defaultFilters = {
            search: '',
            area: 'all',
            olderThanHours: 24,
            limit: 500,
        };
        const allowedAreas = ['all', 'tickets', 'positions', 'documentations'];
        const initialArea = queryString(routeQuery, 'list_area', defaultFilters.area);

        return {
            loading: true,
            analyzing: false,
            deleting: false,
            filtersSidebar: false,
            defaultFilters,
            filters: {
                search: queryString(routeQuery, 'list_search', defaultFilters.search),
                area: allowedAreas.includes(initialArea) ? initialArea : defaultFilters.area,
                olderThanHours: queryNumber(routeQuery, 'list_older_than_hours', defaultFilters.olderThanHours),
                limit: queryNumber(routeQuery, 'list_limit', defaultFilters.limit),
            },
            items: [],
            selectedIds: [],
            summary: {
                total_count: 0,
                total_size_bytes: 0,
                total_size_readable: '0 B',
                has_more: false,
                area: 'all',
            },
            preview: {
                visible: false,
                loading: false,
                url: null,
                item: null,
            },
        };
    },
    computed: {
        areaOptions() {
            return [
                {value: 'all', label: this.$t('Tickets + Positions + Documentations').toString()},
                {value: 'tickets', label: this.$t('Tickets').toString()},
                {value: 'positions', label: this.$t('Positions').toString()},
                {value: 'documentations', label: this.$t('Documentations').toString()},
            ];
        },
        displayedItems() {
            const term = String(this.filters.search || '').trim().toLowerCase();
            if (term === '') {
                return this.items;
            }

            return this.items.filter((item) => {
                const haystack = [
                    String(item.name || ''),
                    String(item.mime || ''),
                    String(item.disk || ''),
                    String(item.path || ''),
                ].join(' ').toLowerCase();

                return haystack.includes(term);
            });
        },
        allSelected() {
            if (this.displayedItems.length < 1) {
                return false;
            }

            return this.displayedItems.every((item) => this.selectedIds.includes(Number(item.id)));
        },
        hasActiveFilters() {
            return String(this.filters.search || '').trim() !== ''
                || String(this.filters.area || 'all') !== String(this.defaultFilters.area)
                || Number(this.filters.olderThanHours || 0) !== Number(this.defaultFilters.olderThanHours)
                || Number(this.filters.limit || 0) !== Number(this.defaultFilters.limit);
        },
        displayStart() {
            return this.displayedItems.length > 0 ? 1 : 0;
        },
        displayEnd() {
            return this.displayedItems.length;
        },
    },
    mounted() {
        this.runAnalysis();
    },
    beforeUnmount() {
        this.revokePreviewUrl();
    },
    methods: {
        openFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        resetFilters() {
            this.filters = {
                search: this.defaultFilters.search,
                area: this.defaultFilters.area,
                olderThanHours: this.defaultFilters.olderThanHours,
                limit: this.defaultFilters.limit,
            };
            this.runAnalysis();
        },
        syncOrphanedImagesRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_search',
                'list_area',
                'list_older_than_hours',
                'list_limit',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_search', String(this.filters.search || '').trim() || null);
            setQueryValue(nextQuery, 'list_area', String(this.filters.area || this.defaultFilters.area) === this.defaultFilters.area ? null : this.filters.area);
            setQueryValue(
                nextQuery,
                'list_older_than_hours',
                Number(this.filters.olderThanHours || 0) === Number(this.defaultFilters.olderThanHours) ? null : Number(this.filters.olderThanHours || 0)
            );
            setQueryValue(
                nextQuery,
                'list_limit',
                Number(this.filters.limit || 0) === Number(this.defaultFilters.limit) ? null : Number(this.filters.limit || 0)
            );

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        runAnalysis() {
            const self = this;
            self.syncOrphanedImagesRouteState();
            self.analyzing = true;

            axios.get('api/workspace/admin/settings/orphaned-images', {
                params: {
                    area: String(self.filters.area || 'all'),
                    older_than_hours: Number(self.filters.olderThanHours || 0),
                    limit: Number(self.filters.limit || 500),
                }
            }).then(function (response) {
                self.items = Array.isArray(response.data.items) ? response.data.items : [];
                self.summary = response.data.summary || self.summary;
                self.selectedIds = self.selectedIds.filter((id) => self.items.some((item) => Number(item.id) === Number(id)));
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while loading data').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.loading = false;
                self.analyzing = false;
            });
        },
        toggleSelectAll(checked) {
            const visibleIds = this.displayedItems.map((item) => Number(item.id));
            if (checked) {
                const merged = new Set([...this.selectedIds, ...visibleIds]);
                this.selectedIds = Array.from(merged);
                return;
            }

            this.selectedIds = this.selectedIds.filter((id) => !visibleIds.includes(Number(id)));
        },
        deleteSingle(item) {
            const self = this;
            if (!item || !item.id) {
                return;
            }
            if (!window.confirm(self.$i18n.t('Delete this file?').toString())) {
                return;
            }

            self.deleting = true;
            axios.delete('api/workspace/admin/settings/orphaned-images/' + Number(item.id), {
                data: {
                    area: String(self.filters.area || 'all'),
                    older_than_hours: Number(self.filters.olderThanHours || 0),
                }
            }).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.runAnalysis();
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while deleting data').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.deleting = false;
            });
        },
        deleteSelected() {
            const self = this;
            if (self.selectedIds.length < 1) {
                return;
            }
            if (!window.confirm(self.$i18n.t('Delete selected files?').toString())) {
                return;
            }

            self.deleting = true;
            axios.delete('api/workspace/admin/settings/orphaned-images', {
                data: {
                    area: String(self.filters.area || 'all'),
                    ids: self.selectedIds,
                    older_than_hours: Number(self.filters.olderThanHours || 0),
                }
            }).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.runAnalysis();
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while deleting data').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.deleting = false;
            });
        },
        deleteAllInScope() {
            const self = this;
            if (Number(self.summary.total_count || 0) < 1) {
                return;
            }
            if (!window.confirm(self.$i18n.t('Delete all currently found files?').toString())) {
                return;
            }

            self.deleting = true;
            axios.delete('api/workspace/admin/settings/orphaned-images', {
                data: {
                    area: String(self.filters.area || 'all'),
                    delete_all: true,
                    older_than_hours: Number(self.filters.olderThanHours || 0),
                }
            }).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.runAnalysis();
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while deleting data').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.deleting = false;
            });
        },
        openPreview(item) {
            const self = this;
            if (!item || !item.preview_endpoint || !item.exists_on_disk || !self.canPreview(item)) {
                return;
            }

            self.revokePreviewUrl();
            self.preview.visible = true;
            self.preview.loading = true;
            self.preview.item = item;

            axios.get(item.preview_endpoint, {
                responseType: 'blob',
            }).then(function (response) {
                self.preview.url = window.URL.createObjectURL(response.data);
            }).catch(function (error) {
                self.preview.url = null;
                if (error && error.response && Number(error.response.status) === 404) {
                    return;
                }
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('Preview not available').toString(),
                    type: 'error'
                });
            }).finally(function () {
                self.preview.loading = false;
            });
        },
        canPreview(item) {
            if (!item) {
                return false;
            }

            return String(item.mime || '').toLowerCase().startsWith('image/');
        },
        hasPreviewSource(item) {
            return !!(item && item.exists_on_disk && item.preview_endpoint);
        },
        hasDownload(item) {
            return !!(item && item.exists_on_disk && item.download_url);
        },
        closePreview() {
            this.preview.visible = false;
            this.preview.item = null;
            this.preview.loading = false;
            this.revokePreviewUrl();
        },
        revokePreviewUrl() {
            if (this.preview.url) {
                window.URL.revokeObjectURL(this.preview.url);
                this.preview.url = null;
            }
        },
        withToken(url) {
            const resolvedUrl = String(url || '');
            if (resolvedUrl === '') {
                return resolvedUrl;
            }

            const token = typeof getAuthToken === 'function' ? getAuthToken() : null;
            if (!token || resolvedUrl.includes('token=')) {
                return resolvedUrl;
            }

            return resolvedUrl + (resolvedUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }

            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
                return String(value);
            }

            return parsed.toLocaleString();
        },
    }
};
</script>
